import { useEffect, useRef } from 'react';
import { Html } from '@react-three/drei';
import { Group } from 'three';

import { applyPose, type CharacterRenderer, type CharacterRendererProps } from '../rendererRegistry';

const BONE_COLOR = '#8a8f98';
const BONE_TIP_COLOR = '#c9cdd3';
const GRID_SPACING = 0.55;
const GRID_COLUMNS = 4;

function BoneGizmo({
  boneId,
  pose,
  index,
}: {
  boneId: string;
  pose: CharacterRendererProps['pose'];
  index: number;
}) {
  const ref = useRef<Group>(null);

  useEffect(() => {
    applyPose(ref.current, pose, boneId);
  }, [pose, boneId]);

  const column = index % GRID_COLUMNS;
  const row = Math.floor(index / GRID_COLUMNS);

  return (
    <group position={[column * GRID_SPACING - 0.8, 1.2 - row * GRID_SPACING, 0]}>
      <group ref={ref}>
        <mesh position={[0, 0.16, 0]} castShadow receiveShadow>
          <capsuleGeometry args={[0.04, 0.24, 4, 8]} />
          <meshStandardMaterial color={BONE_COLOR} roughness={0.6} metalness={0.1} />
        </mesh>
        <mesh castShadow receiveShadow>
          <sphereGeometry args={[0.05, 10, 10]} />
          <meshStandardMaterial color={BONE_TIP_COLOR} roughness={0.5} metalness={0.15} />
        </mesh>
      </group>
      <Html position={[0, -0.18, 0]} center distanceFactor={8} style={{ pointerEvents: 'none' }}>
        <span style={{ fontSize: 10, color: '#c9cdd3', whiteSpace: 'nowrap' }}>{boneId}</span>
      </Html>
    </group>
  );
}

/**
 * A generic per-bone gizmo renderer for a custom rig that has no hand-authored
 * mesh/hierarchy registered with the engine: draws each bone as an
 * independent, labeled capsule so poses can be authored and previewed without
 * the project's own renderer code. This is Studio's authoring-only fallback;
 * a game still renders its characters with its own hand-tuned components.
 */
export function createGenericSkeletonRenderer(boneIds: string[]): CharacterRenderer {
  function GenericSkeletonRenderer({ pose }: CharacterRendererProps) {
    return (
      <group>
        {boneIds.map((boneId, index) => (
          <BoneGizmo key={boneId} boneId={boneId} pose={pose} index={index} />
        ))}
      </group>
    );
  }
  return GenericSkeletonRenderer;
}

/** Deterministic rendererId for a project-registered custom rig's generic preview. */
export function genericSkeletonRendererId(rigId: string): string {
  return `generic-skeleton:${rigId}`;
}
